import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FieldDetailsModal = ({ field, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-500 to-purple-600 p-6 rounded-t-2xl">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/30">
                <Icon name={field?.icon} size={32} className="text-white" />
              </div>
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-1">
                  {field?.name}
                </h2>
                <p className="text-white/80 text-sm">{field?.category}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-md flex items-center justify-center transition-all border border-white/30"
            >
              <Icon name="X" size={20} className="text-white" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 md:p-8">
          {/* Description */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="FileText" size={20} className="text-primary" />
              Description du Domaine
            </h3>
            <p className="text-muted-foreground leading-relaxed">{field?.description}</p>
          </div>

          {/* Career Pathways */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Briefcase" size={20} className="text-primary" />
              Parcours de Carrière
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {field?.careerHighlights?.map((career, index) => (
                <div key={index} className="flex items-center gap-3 bg-muted rounded-lg p-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon name="Target" size={18} className="text-primary" />
                  </div>
                  <span className="text-foreground font-medium">{career}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Skills Required */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Lightbulb" size={20} className="text-primary" />
              Compétences Requises
            </h3>
            <div className="flex flex-wrap gap-2">
              {field?.skillsRequired?.map((skill, index) => (
                <div key={index} className="flex items-center gap-2 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 px-4 py-2 rounded-full">
                  <Icon name="CheckCircle" size={14} />
                  <span className="text-sm font-medium">{skill}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Market Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-900/10 rounded-xl p-5 border border-green-200 dark:border-green-800">
              <div className="flex items-center gap-3 mb-2">
                <Icon name="TrendingUp" size={24} className="text-green-600" />
                <h4 className="font-semibold text-foreground">Demande du Marché</h4>
              </div>
              <p className="text-2xl font-bold text-green-600 capitalize">
                {field?.demandLevel === 'high' ? 'Très Élevée' : field?.demandLevel === 'medium' ? 'Moyenne' : 'Faible'}
              </p>
            </div>
            <div className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-900/10 rounded-xl p-5 border border-amber-200 dark:border-amber-800">
              <div className="flex items-center gap-3 mb-2">
                <Icon name="Coins" size={24} className="text-amber-600" />
                <h4 className="font-semibold text-foreground">Fourchette Salariale</h4>
              </div>
              <p className="text-xl font-bold text-amber-600">{field?.averageSalary}</p>
            </div>
          </div>

          {/* Exploration Progress */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="BarChart" size={20} className="text-primary" />
              Votre Progression d'Exploration
            </h3>
            <div className="bg-muted rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-muted-foreground">Progression</span>
                <span className="text-lg font-bold text-primary">{field?.explorationProgress}%</span>
              </div>
              <div className="w-full h-3 bg-background rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-purple-600 transition-all duration-500"
                  style={{ width: `${field?.explorationProgress}%` }}
                />
              </div>
              {field?.explorationProgress < 100 && (
                <p className="text-xs text-muted-foreground mt-3">
                  Continuez à explorer pour débloquer du contenu exclusif et gagner +{field?.rewardPoints} points
                </p>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="default"
              iconName="School"
              iconPosition="left"
              className="flex-1"
            >
              Voir les Établissements
            </Button>
            <Button
              variant="outline"
              iconName="Users"
              iconPosition="left"
              className="flex-1"
            >
              Interviews d'Experts
            </Button>
            <Button
              variant="outline"
              iconName="Video"
              iconPosition="left"
              className="flex-1"
            >
              Expérience Virtuelle
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FieldDetailsModal;